import { Asset } from '@/api/generatedApi';
import { useReplaceAssetFile } from '@/hooks/api/assets/useReplaceAssetFile';
import { useToast } from '@/hooks/useToast';
import { getAssetFileName } from '@/utils/assets';
import { formatFileSize } from '@/utils/format';
import { getNormalizedFileExtension } from '@/utils/path';
import { MAX_FILE_SIZE } from '@/utils/validation';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

export const ReplaceAssetFileModal = ({
  isOpen,
  onClose,
  asset,
  onReplaced,
}: {
  isOpen: boolean;
  onClose: () => void;
  asset?: Asset;
  onReplaced?: () => void;
}) => {
  const { showToast } = useToast();
  const fileInputReference = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(undefined);
      setErrorMessage(undefined);
      if (fileInputReference.current) {
        fileInputReference.current.value = '';
      }
    }
  }, [isOpen]);

  const { replaceAssetFile, isPending } = useReplaceAssetFile({
    asset,
    onSuccess: () => {
      showToast({
        message: `Replaced "${asset ? getAssetFileName(asset) : ''}". Re-indexing...`,
        type: 'success',
      });
      onReplaced?.();
      onClose();
    },
    onError: message => {
      setErrorMessage(message);
    },
  });

  if (!asset) return;

  const expectedExtension = getNormalizedFileExtension(asset.path);
  const fileName = getAssetFileName(asset);
  const isReplaceSupported = expectedExtension !== '';

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFile(undefined);
      return;
    }
    setErrorMessage(undefined);

    if (getNormalizedFileExtension(file.name) !== expectedExtension) {
      setErrorMessage(`File must have a ${expectedExtension} extension.`);
      setSelectedFile(undefined);
      if (fileInputReference.current) {
        fileInputReference.current.value = '';
      }
      return;
    }
    if (file.size === 0) {
      setErrorMessage('Selected file is empty.');
      setSelectedFile(undefined);
      if (fileInputReference.current) {
        fileInputReference.current.value = '';
      }
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setErrorMessage(`File is too large. Maximum size is ${formatFileSize(MAX_FILE_SIZE)}.`);
      setSelectedFile(undefined);
      if (fileInputReference.current) {
        fileInputReference.current.value = '';
      }
      return;
    }
    setSelectedFile(file);
  };

  const handleSubmit = () => {
    if (!selectedFile) return;
    replaceAssetFile(selectedFile);
  };

  const handleClose = () => {
    if (!isPending) {
      onClose();
    }
  };

  const handleBrowseClick = () => {
    fileInputReference.current?.click();
  };

  return (
    <Dialog
      fullWidth
      maxWidth='sm'
      open={isOpen}
      onClose={handleClose}
      aria-modal='true'
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
          },
        },
      }}
    >
      <DialogTitle>Replace File</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          '&:first-of-type': {
            paddingTop: 3,
          },
        }}
      >
        {errorMessage && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          Replacing
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            mb: 2,
          }}
        >
          <InsertDriveFileIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant='body2' noWrap>
            {fileName}
          </Typography>
        </Box>

        {!isReplaceSupported && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            Replace File is unavailable for assets without a file extension.
          </Alert>
        )}

        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          New file{isReplaceSupported ? ` (must be ${expectedExtension})` : ''}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
          }}
        >
          <UploadFileIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant='body2' noWrap sx={{ flex: 1 }} color={selectedFile ? 'text.primary' : 'text.secondary'}>
            {selectedFile?.name ?? 'No file selected'}
          </Typography>
          <Button size='small' onClick={handleBrowseClick} disabled={isPending || !isReplaceSupported}>
            {selectedFile ? 'Change' : 'Choose file'}
          </Button>
        </Box>
        {selectedFile && selectedFile.name !== fileName && (
          <Alert severity='info' variant='outlined' sx={{ mt: 2 }}>
            Will be saved as <strong>{fileName}</strong> — the asset keeps its existing name and ID.
          </Alert>
        )}
        <input
          ref={fileInputReference}
          type='file'
          accept={expectedExtension}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          data-testid='replaceAssetFileInput'
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={!selectedFile || isPending || !isReplaceSupported}
          sx={{ width: '110px' }}
        >
          {isPending ? <CircularProgress size={20} /> : 'Replace'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
